import { useCallback } from 'react';
import { useBrokerIsExternal } from '@/common/hooks/useBrokerIsExternal';
import { useHasAccessToFeature } from '@/common/hooks/useHasAccessToFeature';
import { AppFeature } from '@/common/hooks/useHasAccessToFeature/types';
import { useCore } from '$api-clients/hooks';
import { DEFAULT_ORDER } from './constants';
import { buildQuickLinks } from './helpers';
import { QuickLinkName } from './types';

export const useGetActiveQuickLinks = () => {
  const hasAccessToLeases = useHasAccessToFeature(AppFeature.LEASES);
  const { isExternal: brokerIsExternal } = useBrokerIsExternal();
  const {
    data = [],
    isLoading,
    error,
    mutate,
  } = useCore('/users/me/quick-links');
  const uniqueQuickLinks = Array.from(
    new Set(data.map((link) => link.quickLink).concat(DEFAULT_ORDER)),
  );

  const quickLinks = uniqueQuickLinks
    .filter((link): link is QuickLinkName =>
      Object.values(QuickLinkName).includes(link as QuickLinkName),
    )
    .filter((link) => {
      if (link === QuickLinkName.CREATE_LEASE) return hasAccessToLeases;
      // GROWTH_HUB is the "Side Stack & Marketplace" link.
      if (link === QuickLinkName.GROWTH_HUB) return !brokerIsExternal;
      return true;
    });

  const updateCache = useCallback(
    (newQuickLinkList: QuickLinkName[]) => {
      const newResults = newQuickLinkList.map((link, index) => ({
        quickLink: link,
        order: index,
      }));

      void mutate(newResults);
    },
    [mutate],
  );

  return { quickLinks, isLoading, error, updateCache };
};

export const useQuickLinksSection = () => {
  const { quickLinks, isLoading } = useGetActiveQuickLinks();

  const activeQuickLinks = quickLinks.slice(0, 5);

  return {
    quickLinks: buildQuickLinks(activeQuickLinks),
    loading: isLoading,
  };
};
